import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { slideInAnimation } from './shared/animations/page-animation';
import { TranslateService } from '@ngx-translate/core';
import { AuthHelper } from './shared/helpers/auth.helper';
import { SeoService } from './shared/services/seo.service';
import { StructuredDataService } from './shared/services/structured-data.service';
import { seoConfig, defaultSEO, organizationSchema, websiteSchema } from './shared/config/seo.config';
import { Subject } from 'rxjs';
import { filter, map, mergeMap, takeUntil } from 'rxjs/operators';
import { NotificationsService } from './notifications/services/notifications.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet],
  animations: [slideInAnimation],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'SIMVet Monitor';
  translate = inject(TranslateService);
  authHelper = inject(AuthHelper);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private seoService = inject(SeoService);
  private structuredDataService = inject(StructuredDataService);
  private notificationsService = inject(NotificationsService);
  private destroy$ = new Subject<void>();
  private readonly supportedLangs = ['en', 'es', 'pt', 'fr', 'de', 'it'];

  constructor() {
    this.translate.addLangs(this.supportedLangs);
    this.translate.setFallbackLang('en');

    // Try to get saved language preference from user profile
    const userToken = this.authHelper.getUserToken();
    const savedLanguage = userToken?.user?.language;
    let defaultLang = 'en';
    if (savedLanguage && this.supportedLangs.includes(savedLanguage)) {
      // Use saved language preference
      defaultLang = savedLanguage;
    } else {
      // Get browser language as fallback
      const browserLang = this.getBrowserLanguage();
      defaultLang = this.supportedLangs.includes(browserLang) ? browserLang : 'en';
    }
    this.translate.use(defaultLang);
  }

  ngOnInit(): void {
    // Initialize default SEO
    this.initializeSEO();

    // Start notification polling if user is logged in
    if (this.authHelper.isUserLogged()) {
      // this.notificationsService.startPolling();
    }

    // Listen to route changes and update SEO
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map(route => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        mergeMap(route => route.data),
        takeUntil(this.destroy$)
      )
      .subscribe(data => {
        this.updateRouteSEO(data);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeSEO(): void {
    // Set default SEO
    this.seoService.updateSEO(defaultSEO);

    // Inject organization schema
    this.structuredDataService.injectOrganizationSchema(organizationSchema);

    // Inject website schema
    this.structuredDataService.injectWebSiteSchema(websiteSchema);
  }

  private updateRouteSEO(routeData: any): void {
    const currentUrl = this.router.url;
    const routePath = currentUrl.split('?')[0]; // Remove query params

    // Get SEO data from route data or config
    let seoData = routeData?.seo;

    // If no SEO data in route, check config
    if (!seoData) {
      // Try to match route path in config
      const configKey = Object.keys(seoConfig).find(key => routePath.startsWith(key));
      if (configKey) {
        seoData = seoConfig[configKey];
      }
    }

    // Merge with defaults and update
    const translatedTitle = routeData?.titleKey
      ? this.translate.instant(routeData.titleKey)
      : undefined;
    if (seoData) {
      const mergedSEO = {
        ...defaultSEO,
        ...seoData,
        ...(translatedTitle != null && { title: translatedTitle }),
        url: `https://simvetmonitor.com${routePath}`,
        canonical: routePath,
      };
      this.seoService.updateSEO(mergedSEO);
    } else if (translatedTitle) {
      this.seoService.updateSEO({
        ...defaultSEO,
        title: translatedTitle,
        url: `https://simvetmonitor.com${routePath}`,
        canonical: routePath,
      });
    } else {
      // Use default with current URL
      this.seoService.updateSEO({
        ...defaultSEO,
        url: `https://simvetmonitor.com${routePath}`,
        canonical: routePath,
      });
    }

    // Update hreflang tags for multilingual support
    this.updateHreflangTags(routePath);

    // Update breadcrumb schema if breadcrumb data exists
    if (routeData?.breadcrumb) {
      this.updateBreadcrumbSchema(routePath, routeData.breadcrumb);
    }
  }

  private updateHreflangTags(routePath: string): void {
    const baseUrl = 'https://simvetmonitor.com';
    const alternateUrls = this.supportedLangs.map(lang => ({
      lang: lang === 'en' ? 'en' : lang,
      url: `${baseUrl}${routePath}?lang=${lang}`,
    }));

    this.seoService.updateHreflangTags(alternateUrls);
  }

  private updateBreadcrumbSchema(routePath: string, breadcrumb: string | string[]): void {
    const breadcrumbs: Array<{ name: string; url: string }> = [
      { name: 'Home', url: 'https://simvetmonitor.com/home' },
    ];

    if (Array.isArray(breadcrumb)) {
      breadcrumbs.push(...breadcrumb.map((crumb, index) => ({
        name: crumb,
        url: `https://simvetmonitor.com${routePath.split('/').slice(0, index + 2).join('/')}`,
      })));
    } else if (breadcrumb) {
      breadcrumbs.push({
        name: breadcrumb,
        url: `https://simvetmonitor.com${routePath}`,
      });
    }

    this.structuredDataService.injectBreadcrumbSchema(breadcrumbs);
  }

  private getBrowserLanguage(): string {
    // Try to get language from browser
    if (typeof navigator !== 'undefined') {
      // Get the primary language (e.g., 'en-US' -> 'en')
      const lang = navigator.language || (navigator as any).userLanguage;
      if (lang) {
        // Extract language code (first 2 characters)
        return lang.split('-')[0].toLowerCase();
      }

      // Fallback to navigator.languages if available 
      if (navigator.languages && navigator.languages.length > 0) {
        return navigator.languages[0].split('-')[0].toLowerCase();
      }
    }
    console.log('No language found, defaulting to en');

    // Default fallback
    return 'en';
  }

  prepareRoute(outlet: RouterOutlet) {
    return outlet && outlet.activatedRouteData && outlet.activatedRouteData['animation'];
  }
}
